import { createApp } from '@backstage/frontend-defaults';
import appPlugin from '@backstage/plugin-app';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import spokeRequestsPlugin from '@internal/plugin-spoke-requests';
import spokeOverviewPlugin from '@internal/plugin-spoke-overview';
import roadmapPlugin from '@internal/plugin-roadmap';
import { navModule } from './modules/nav';
import { scaffolderCustomizations } from './scaffolder/scaffolderModule';

import { createFrontendModule } from '@backstage/frontend-plugin-api';
import {
  SignInPageBlueprint,
  SignInPageProps,
  ThemeBlueprint,
} from '@backstage/plugin-app-react';
import { SignInPage } from '@backstage/core-components';
import {
  configApiRef,
  microsoftAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import LightIcon from '@material-ui/icons/WbSunny';
import { HowdenThemeProvider } from './themes/howdenTheme';
import { DataSphereLandingPage } from './modules/landing/DataSphereLandingPage';
import { DevPersonaSignInPage } from './modules/devPersona/DevPersonaSignInPage';
import './howdenStyles.css';
import { databaseAdminPlugin } from './modules/databaseAdmin/plugin';

// In e2e test mode (see app-config.e2e.yaml) fall back to the stock
// Backstage sign-in chrome with a guest provider, so Playwright can
// authenticate without a real Microsoft account — see
// e2e-tests/spoke-request-wizard.ado.e2e.ts, which clicks the default
// "Enter" guest button. Everywhere else, show the pretty DataSphere
// marketing landing page with a Microsoft sign-in call to action.
const HowdenSignInPage = (props: SignInPageProps) => {
  const configApi = useApi(configApiRef);
  const e2eTestMode =
    configApi.getOptionalBoolean('scaffolder.e2eTestMode') ?? false;

  if (e2eTestMode) {
    return (
      <SignInPage
        {...props}
        providers={[
          'guest',
          {
            id: 'microsoft-auth-provider',
            title: 'Microsoft',
            message: 'Sign in with your Howden Microsoft account',
            apiRef: microsoftAuthApiRef,
          },
        ]}
      />
    );
  }

  if (configApi.getOptionalBoolean('datasphere.devPersonaSwitcher')) {
    return <DevPersonaSignInPage {...props} />;
  }

  return <DataSphereLandingPage {...props} />;
};

const microsoftSignInModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    SignInPageBlueprint.make({
      params: {
        loader: async () => HowdenSignInPage,
      },
    }),
  ],
});

const howdenTheme = ThemeBlueprint.make({
  name: 'howden',
  params: {
    theme: {
      id: 'howden',
      title: 'Howden',
      variant: 'light',
      icon: <LightIcon />,
      Provider: ({ children }) => (
        <HowdenThemeProvider>{children}</HowdenThemeProvider>
      ),
    },
  },
});

const howdenThemeModule = createFrontendModule({
  pluginId: 'app',
  extensions: [howdenTheme],
});

const disableDefaultThemesModule = createFrontendModule({
  pluginId: 'app',
  extensions: [
    appPlugin.getExtension('theme:app/light').override({ disabled: true }),
    appPlugin.getExtension('theme:app/dark').override({ disabled: true }),
  ],
});

export default createApp({
  features: [
    catalogPlugin,
    spokeRequestsPlugin,
    spokeOverviewPlugin,
    roadmapPlugin,
    databaseAdminPlugin,
    navModule,
    disableDefaultThemesModule,
    howdenThemeModule,
    scaffolderCustomizations,
    microsoftSignInModule,
  ],
});
